import { type Card, type Suit, SUITS, isJoker, suitCounts } from '../trick/cards.js';
import { isWild } from './play.js';
import type { CrazyEightsView } from './view.js';

/** `easy` plays any legal card and names a random suit. `clever` saves its wild cards (eights and Jokers) and keeps its longest suit. */
export type CrazyEightsRobotLevel = 'easy' | 'clever';

export type CrazyEightsAction =
  | { readonly type: 'play'; readonly card: Card; readonly suit?: Suit }
  | { readonly type: 'draw' }
  | { readonly type: 'pass' };

/** The suit a robot holds most of (ties: the first in suit order); used to name a suit after a wild card. A Joker has no suit and is not counted. */
function bestSuit(hand: readonly Card[], fallback: Suit): Suit {
  const counts = suitCounts(hand.filter((c) => !isJoker(c)));
  const best = SUITS.reduce((a, b) => (counts[b] > counts[a] ? b : a), SUITS[0] as Suit);
  return counts[best] > 0 ? best : fallback;
}

/**
 * What a robot does on its turn: it sees only its own `CrazyEightsView`. `random` is used by the easy robot only, so a
 * clever game is fully deterministic.
 */
export function crazyEightsChoose(view: CrazyEightsView, level: CrazyEightsRobotLevel, random: () => number): CrazyEightsAction {
  if (!view.canAct) throw new Error("crazyEightsChoose: not this seat's turn");
  const legal = view.legalPlays;
  if (legal.length === 0) {
    if (view.canDraw) return { type: 'draw' };
    return { type: 'pass' };
  }
  const withSuit = (card: Card, hand: readonly Card[]): CrazyEightsAction =>
    isWild(card)
      ? { type: 'play', card, suit: level === 'easy' ? (SUITS[Math.floor(random() * SUITS.length)] as Suit) : bestSuit(hand.filter((c) => c !== card), view.activeSuit) }
      : { type: 'play', card };
  if (level === 'easy') return withSuit(legal[Math.floor(random() * legal.length)] as Card, view.hand);

  // Clever: play a plain card when it can (wild cards are the way out of trouble), preferring a card from the longest suit,
  // and play a wild card only when nothing else fits.
  const plain = legal.filter((c) => !isWild(c));
  const pool = plain.length > 0 ? plain : legal;
  const counts = suitCounts(view.hand.filter((c) => !isJoker(c)));
  const card = pool.reduce((a, b) => (counts[b.suit] > counts[a.suit] || (counts[b.suit] === counts[a.suit] && b.rank > a.rank) ? b : a));
  return withSuit(card, view.hand);
}
